import React, { useCallback, useEffect, useState } from "react";
import { supabase } from "../../supabase";
import { shouldSkipSupabase } from "../../helpers/guestMode";
import { newFinanceIdempotencyKey } from "../../helpers/financeActivity";
import { listFinanceAccounts } from "../../helpers/financeAccounts";
import { createTransferEvent } from "../../helpers/financeIngestion";
import { resolveFinanceOrganizationIdFromActor } from "../../helpers/nexoraOpsAccess";
import { usePermissions } from "../../hooks/usePermissions";
import { CPDropdown } from "../CPDropdown";
import { useIsDesktopViewport } from "../../hooks/useIsDesktopViewport";
import { FinanceFormDesktop } from "./FinanceDesktop";
import {
  FinanceBackButton,
  FINANCE_CARD_STYLE,
  FINANCE_HEADER_STYLE,
  FINANCE_INPUT_STYLE,
  FINANCE_LABEL_STYLE,
  FINANCE_PRIMARY_BTN_STYLE,
  FINANCE_SCREEN_STYLE,
  FINANCE_SECONDARY_BTN_STYLE,
  FINANCE_SUBTITLE_STYLE,
  FINANCE_TITLE_STYLE,
} from "./financeUi";

function todayIsoDate() {
  return new Date().toISOString().slice(0, 10);
}

/**
 * Transfer money between two accounts — one ingestion event, two movements.
 */
export function FinanceTransferScreen({ currentUser, onBack, onSuccess }) {
  const isDesktop = useIsDesktopViewport();
  const { actor } = usePermissions();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [fromAccountId, setFromAccountId] = useState("");
  const [toAccountId, setToAccountId] = useState("");
  const [amount, setAmount] = useState("");
  const [occurredOn, setOccurredOn] = useState(todayIsoDate());
  const [description, setDescription] = useState("");
  const [notes, setNotes] = useState("");

  const load = useCallback(async () => {
    if (shouldSkipSupabase(currentUser)) {
      setAccounts([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const rows = await listFinanceAccounts(supabase, {
        includeInactive: false,
      });
      setAccounts(rows);
      setError(null);
    } catch (err) {
      console.error("[Finance] transfer load failed:", err);
      setError("Unable to load accounts.");
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!fromAccountId && accounts[0]?.id) {
      setFromAccountId(accounts[0].id);
    }
    if (!toAccountId && accounts[1]?.id) {
      setToAccountId(accounts[1].id);
    }
  }, [accounts, fromAccountId, toAccountId]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError(null);

    try {
      const organizationId = resolveFinanceOrganizationIdFromActor(
        actor || currentUser,
      );
      if (!organizationId) {
        throw new Error("Unable to resolve organization.");
      }
      if (!fromAccountId || !toAccountId) {
        throw new Error("Select both accounts.");
      }
      if (fromAccountId === toAccountId) {
        throw new Error("Choose two different accounts.");
      }
      const amountValue = Number(amount);
      if (!Number.isFinite(amountValue) || amountValue <= 0) {
        throw new Error("Enter an amount greater than zero.");
      }

      await createTransferEvent(supabase, {
        organization_id: organizationId,
        from_account_id: fromAccountId,
        to_account_id: toAccountId,
        amount: amountValue,
        occurred_on: occurredOn || todayIsoDate(),
        description: description.trim() || "Transfer",
        notes: notes.trim() || null,
        idempotency_key: newFinanceIdempotencyKey("transfer"),
      });

      if (typeof onSuccess === "function") {
        onSuccess();
      } else if (typeof onBack === "function") {
        onBack();
      }
    } catch (err) {
      console.error("[Finance] transfer save failed:", err);
      setError(err?.message || "Unable to save transfer.");
    } finally {
      setBusy(false);
    }
  };

  if (isDesktop) {
    const accountOptions = [
      { value: "", label: "Select account" },
      ...accounts.map((account) => ({ value: account.id, label: account.name })),
    ];
    return (
      <FinanceFormDesktop
        title="Transfer"
        subtitle="Move money from one account to another."
        onBack={onBack}
        error={error}
        loading={loading}
        onSubmit={handleSubmit}
        submitLabel="Save Transfer"
        submitDisabled={busy || accounts.length < 2}
        busy={busy}
        onCancel={onBack}
        hint={accounts.length < 2 ? "You need at least two accounts to transfer money." : null}
      >
        <CPDropdown label="From" value={fromAccountId} onChange={setFromAccountId} required={true} options={accountOptions} />
        <CPDropdown label="To" value={toAccountId} onChange={setToAccountId} required={true} options={accountOptions} />
        <label className="fin-field">
          <span>Amount</span>
          <input type="number" step="0.01" min="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" required />
        </label>
        <label className="fin-field">
          <span>Date</span>
          <input type="date" value={occurredOn} onChange={(e) => setOccurredOn(e.target.value)} required />
        </label>
        <label className="fin-field fin-form__full">
          <span>Description</span>
          <input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Tax set-aside, move to savings…" />
        </label>
        <label className="fin-field fin-form__full">
          <span>Notes (optional)</span>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} />
        </label>
      </FinanceFormDesktop>
    );
  }

  return (
    <div style={FINANCE_SCREEN_STYLE}>
      <FinanceBackButton onClick={onBack} />

      <div style={FINANCE_HEADER_STYLE}>
        <h1 style={FINANCE_TITLE_STYLE}>Transfer</h1>
        <p style={FINANCE_SUBTITLE_STYLE}>
          Move money from one account to another.
        </p>
      </div>

      {error ? (
        <p style={{ color: "#f87171", marginBottom: "12px" }}>{error}</p>
      ) : null}

      {loading ? (
        <p style={{ opacity: 0.7 }}>Loading…</p>
      ) : (
        <form
          onSubmit={handleSubmit}
          style={{
            ...FINANCE_CARD_STYLE,
            display: "flex",
            flexDirection: "column",
            gap: "12px",
            maxWidth: "480px",
          }}
        >
          <CPDropdown
            label="From"
            value={fromAccountId}
            onChange={setFromAccountId}
            required={true}
            options={[
              { value: "", label: "Select account" },
              ...accounts.map((account) => ({
                value: account.id,
                label: account.name,
              })),
            ]}
          />

          <CPDropdown
            label="To"
            value={toAccountId}
            onChange={setToAccountId}
            required={true}
            options={[
              { value: "", label: "Select account" },
              ...accounts.map((account) => ({
                value: account.id,
                label: account.name,
              })),
            ]}
          />

          <label>
            <span style={FINANCE_LABEL_STYLE}>Amount</span>
            <input
              style={FINANCE_INPUT_STYLE}
              type="number"
              step="0.01"
              min="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              required
            />
          </label>

          <label>
            <span style={FINANCE_LABEL_STYLE}>Date</span>
            <input
              style={FINANCE_INPUT_STYLE}
              type="date"
              value={occurredOn}
              onChange={(e) => setOccurredOn(e.target.value)}
              required
            />
          </label>

          <label>
            <span style={FINANCE_LABEL_STYLE}>Description</span>
            <input
              style={FINANCE_INPUT_STYLE}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Tax set-aside, move to savings…"
            />
          </label>

          <label>
            <span style={FINANCE_LABEL_STYLE}>Notes (optional)</span>
            <textarea
              style={{ ...FINANCE_INPUT_STYLE, minHeight: "72px" }}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </label>

          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
            <button
              type="submit"
              style={FINANCE_PRIMARY_BTN_STYLE}
              disabled={busy || accounts.length < 2}
            >
              {busy ? "Saving…" : "Save Transfer"}
            </button>
            <button
              type="button"
              style={FINANCE_SECONDARY_BTN_STYLE}
              onClick={onBack}
              disabled={busy}
            >
              Cancel
            </button>
          </div>

          {accounts.length < 2 ? (
            <p style={{ margin: 0, opacity: 0.65, fontSize: "13px" }}>
              You need at least two accounts to transfer money.
            </p>
          ) : null}
        </form>
      )}
    </div>
  );
}
